import { useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import { parseFieldDef } from "@/lib/parseFieldDef";

function renameFieldNodes(editor: Editor, startPos: number, currentId: string, newId: string) {
  const { doc, tr } = editor.state;
  let stop = false;
  doc.nodesBetween(startPos, doc.content.size, (n, nPos) => {
    if (stop) {
      return false;
    }
    if (
      n.type.name === "section" ||
      n.type.name === "heading" ||
      n.type.name === "scoregroupNode"
    ) {
      stop = true;
      return false;
    }
    if (n.type.name === "fieldNode") {
      const fieldDef = parseFieldDef((n.attrs.fieldDef as string | undefined) ?? "");
      if (fieldDef?.type === "score" && fieldDef.scoregroupId === currentId) {
        const updated = { ...fieldDef, scoregroupId: newId };
        tr.setNodeMarkup(nPos, undefined, { ...n.attrs, fieldDef: JSON.stringify(updated) });
      }
    }
    return true;
  });
  if (tr.steps.length > 0) {
    editor.view.dispatch(tr);
  }
}

function ScoreGroupEditForm({
  draftId,
  onCancel,
  onSave,
  setDraftId,
}: Readonly<{
  draftId: string;
  setDraftId: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}>) {
  return (
    <div className="scoregroup-node-edit">
      <input
        className="scoregroup-node-input"
        value={draftId}
        onChange={(e) => {
          setDraftId(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSave();
          }
          if (e.key === "Escape") {
            onCancel();
          }
        }}
        autoFocus
      />
      <button className="scoregroup-node-btn" onClick={onSave}>
        Save
      </button>
      <button className="scoregroup-node-btn" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function useScoregroupSave(
  draftId: string,
  currentId: string,
  updateAttributes: NodeViewProps["updateAttributes"],
  editor: NodeViewProps["editor"],
  node: NodeViewProps["node"],
  getPos: NodeViewProps["getPos"],
  setEditing: (v: boolean) => void,
) {
  return () => {
    const newId = draftId.trim();
    if (!newId || newId === currentId) {
      setEditing(false);
      return;
    }
    updateAttributes({ id: newId });
    const pos = getPos();
    if (pos !== undefined) {
      renameFieldNodes(editor, pos + node.nodeSize, currentId, newId);
    }
    setEditing(false);
  };
}

export function ScoreGroupNodeView({
  editor,
  getPos,
  node,
  updateAttributes,
}: Readonly<NodeViewProps>) {
  const [editing, setEditing] = useState(false);
  const [draftId, setDraftId] = useState("");
  const currentId = node.attrs.id as string;
  const handleSave = useScoregroupSave(
    draftId,
    currentId,
    updateAttributes,
    editor,
    node,
    getPos,
    setEditing,
  );

  return (
    <NodeViewWrapper className="scoregroup-node">
      <div className="scoregroup-node-inner">
        {!editing && (
          <button
            className="scoregroup-node-btn edit"
            title="Rename score group"
            onClick={() => {
              setDraftId(currentId);
              setEditing(true);
            }}
          >
            ✏
          </button>
        )}
        <span className="scoregroup-node-label">Score Group</span>
        {editing ? (
          <ScoreGroupEditForm
            draftId={draftId}
            setDraftId={setDraftId}
            onSave={handleSave}
            onCancel={() => {
              setEditing(false);
            }}
          />
        ) : (
          <code className="scoregroup-node-id">{currentId}</code>
        )}
      </div>
    </NodeViewWrapper>
  );
}
